import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { getExpensePolicy } from 'src/components/ExpensePolicyUploadBox/expensePolicy.network'
import { useQueryManagerReviewRules } from 'src/feature/ManagerReview/managerReviewRules.queries'
import { ExpensePolicyResponse } from 'src/models/organizations.models'
import { useHasSaasFeatures } from 'src/queries/organizations/saas.queries'
import {
  orgQueryKeys,
  transactionsQueryKeys,
} from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  MutationMethod,
  RequestConfig,
  useExtendDelete,
  useExtendMutation,
} from 'src/queries/queries.utils'
import { useQueryOwnUser } from 'src/queries/users.queries'

const refreshTransactionPolicyInsights = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  const policyInsightFamilies = [
    transactionsQueryKeys.lists,
    transactionsQueryKeys.details,
  ]

  policyInsightFamilies.forEach((queryKey) => {
    queryClient.removeQueries({ queryKey, type: 'inactive' })
    void queryClient.invalidateQueries({ queryKey })
  })
}

interface QueryOrgExpensePolicyOptions
  extends ExtendBaseQueryOptions<ExpensePolicyResponse> {
  orgId: string | undefined
}

export const expensePolicyQueries = {
  detail: (orgId: string | undefined) =>
    queryOptions({
      queryKey: orgQueryKeys.expensePolicy(orgId),
      queryFn: () => {
        if (!orgId) {
          throw new Error('expensePolicyQueries.detail: orgId is required')
        }
        return getExpensePolicy(orgId)
      },
      enabled: orgId !== undefined,
    }),
}

export const useQueryExpensePolicy = ({
  orgId,
  enabled = true,
  // queryCacheOptions is a legacy ExtendBaseQueryOptions field the old
  // useExtendQuery wrapper never applied (it fell through to useQuery, which
  // ignores it); strip it so it does not leak into the migrated options.
  queryCacheOptions: _queryCacheOptions,
  ...rest
}: QueryOrgExpensePolicyOptions) => {
  return useQuery({
    ...expensePolicyQueries.detail(orgId),
    // Preserve the legacy arg-presence guard: a spread `enabled` fully replaces
    // the factory's, so re-AND the orgId guard in.
    enabled: enabled && orgId !== undefined,
    ...rest,
  })
}

export const useAIPolicyAssistantEnabled = (): boolean => {
  const { data: user } = useQueryOwnUser({})

  const hasSaasFeatures = useHasSaasFeatures()

  const { data: policyAssistantRules } = useQueryManagerReviewRules({
    orgId: user?.organizationId,
    // Conditionally enable this query for only Pro/Enterprise tiers or Saas-excluded orgs,
    // otherwise this will show the SaaS upgrade modal on every render
    enabled: hasSaasFeatures,
  })

  return !!policyAssistantRules?.policyAssistantEnabled
}

export const usePostExpensePolicy = ({
  orgId,
}: QueryOrgExpensePolicyOptions) => {
  const queryClient = useQueryClient()
  return useExtendMutation<ExpensePolicyResponse, { body: FormData }>(
    MutationMethod.POST,
    {
      mutationUrl: () => `/organizations/${orgId}/expensepolicy`,
      requestConfig: RequestConfig.Form,
      onSuccess: (data) => {
        queryClient.setQueryData<ExpensePolicyResponse>(
          orgQueryKeys.expensePolicy(data.organizationId),
          data,
        )
        refreshTransactionPolicyInsights(queryClient)
      },
    },
  )
}

export const useDeleteExpensePolicy = ({
  orgId,
}: QueryOrgExpensePolicyOptions) => {
  const queryClient = useQueryClient()
  return useExtendDelete<void, { orgId?: string }>({
    mutationUrl: (variables) =>
      `/organizations/${variables.orgId ?? orgId}/expensepolicy`,
    onSuccess: (_data, variables) => {
      const deletedOrgId = variables.orgId ?? orgId

      queryClient.removeQueries({
        queryKey: orgQueryKeys.expensePolicy(deletedOrgId),
      })
      refreshTransactionPolicyInsights(queryClient)
    },
  })
}
